import deepequals from 'fast-deep-equal';
import { DateRange } from 'rewire-common';
import {
  INameEntity,
  IReport,
  IReportFormatInput,
  MessageLevel,
  ReportExtension,
  ReasonCategory,
  ReasonCategories,
  ScheduleFilterInput,
  ReportFilterInput,
  ScheduleSort,
  FieldFilter,
} from '../DataTypes';
import { IRouteManagerState, reportRouteManager, RouteManagerBase } from '../main-navigation/RouteManager';
import MasterDetailModel from '../settings/MasterDetailModel';
import udfsSubscriptionService from '../services/UdfsSubscriptionService';
import { isSame } from '../utility/DateUtils';
import reportFormatsModel from './ReportFormatsModel';
import { reportOptionsModelMap } from './ReportOptions';
import ReportOptionsModel from './ReportOptionsModel';
import DataQueries from '../graphql/Queries';
import mainDateRangeNavModel from '../ui/MainDateRangeNavModel';

export const schema = `
  id
  code
  name
  path
  reportId
  type
  tags
  fields
`;

interface IReportModelState {
  renderedReportId: string;
  rendering: boolean;
  propertiesHaveChanged: boolean;
  reportOptionsModel?: ReportOptionsModel;
  reportExtension: ReportExtension;
  shouldTriggerDownload: boolean;
  secondaryFilters: any;
}

export class ReportModel extends MasterDetailModel<IReportModelState> {
  constructor() {
    super();
  }

  triggerDownload = (downloadLink: any) => {
    downloadLink.click();
    this.shouldTriggerDownload = false;
  };

  pulseRefresh = () => {
    this.renderedReportId = '';
  };

  get shouldTriggerDownload(): boolean {
    return this.state.shouldTriggerDownload;
  }

  set shouldTriggerDownload(value: boolean) {
    this.state.shouldTriggerDownload = value;
  }

  get renderedReportId(): string {
    return this.state.renderedReportId;
  }

  set renderedReportId(value: string) {
    this.state.renderedReportId = value;
  }

  get reportExtension(): ReportExtension {
    return this.state.reportExtension;
  }

  set reportExtension(value: ReportExtension) {
    this.state.reportExtension = value;
  }

  get rendering(): boolean {
    return this.state.rendering;
  }

  set rendering(value: boolean) {
    this.state.rendering = value;
  }

  get reportOptionsModel(): ReportOptionsModel | undefined {
    return this.state.reportOptionsModel;
  }

  set reportOptionsModel(value: ReportOptionsModel | undefined) {
    this.state.reportOptionsModel = value;
  }

  get propertiesHaveChanged(): boolean {
    return this.state.propertiesHaveChanged;
  }

  set propertiesHaveChanged(value: boolean) {
    this.state.propertiesHaveChanged = value;
  }

  get secondaryFilters(): any {
    return this.state.secondaryFilters;
  }

  set secondaryFilters(value: any) {
    this.state.secondaryFilters = value;
  }

  get hasPermittedSecondaryFilters() {
    return this.permittedSecondaryFilters().length;
  }

  get hasSecondaryFilters() {
    const customSecondaryFilters = this.secondaryFilters?.custom;
    return (
      this.hasPermittedSecondaryFilters &&
      customSecondaryFilters?.some((csf: FieldFilter) => this.permittedSecondaryFilters().includes(csf.field))
    );
  }

  private _canRunReport(): boolean {
    return !mainDateRangeNavModel.hasDateError && reportRouteManager.effective && (this.rendering || this.selectedItem?.reportId);
  }

  canRender(): boolean {
    return !this.renderedReportId && this._canRunReport();
  }

  canRefresh(): boolean {
    return !!this.renderedReportId && this._canRunReport();
  }

  load = async (prevState: IRouteManagerState) => {
    const { route, effective, groups, filters, sorts, options, formats, reportSecondaryFilters, selectedReportId } =
      reportRouteManager;
    this.propertiesHaveChanged = this.shouldLoad(
      prevState,
      route,
      effective,
      filters,
      groups,
      sorts,
      options,
      formats,
      reportSecondaryFilters,
      selectedReportId,
    );
    this.secondaryFilters = reportSecondaryFilters;

    // if a different report is selected, reset the format options so defaults will be used
    if (selectedReportId !== (prevState as any).selectedReportId) {
      reportFormatsModel.reset();
    }

    if (this.propertiesHaveChanged) {
      this.reportOptionsModel = undefined;
      this.renderedReportId = '';
      return this.select(selectedReportId);
    }
  };

  private shouldLoad(
    prevState: IRouteManagerState,
    route: string | undefined,
    effective: DateRange | undefined,
    filters: ScheduleFilterInput | undefined,
    groups: string[] | undefined,
    sorts: ScheduleSort | undefined,
    options: any,
    formats: any,
    reportSecondaryFilters: any,
    selectedReportId?: string,
  ): boolean {
    if (!isSame(effective, prevState.effective)) return true;
    if (this.state.items.length === 0) return true;
    if (selectedReportId !== (prevState as any).selectedReportId) return true;
    if (prevState.route !== route) return true;
    if (!deepequals(prevState.filters, filters)) return true;
    if (!deepequals(prevState.groups, groups)) return true;
    if (!deepequals(prevState.sorts, sorts)) return true;
    if (!deepequals((prevState as any).options, options)) return true;
    if (!deepequals((prevState as any).formats, formats)) return true;
    if (!deepequals((prevState as any).reportSecondaryFilters, reportSecondaryFilters)) return true;
    return false;
  }

  refreshReport = async () => {
    if (!this.canRefresh()) return;
    await this._runReport();
  };

  renderReport = async () => {
    if (!this.canRender()) return;
    await this._runReport();
  };

  private async _runReport() {
    try {
      this.rendering = true;
      const reportId = this.selectedItem?.reportId;
      if (!reportId) return;

      const { effective, filters } = reportRouteManager;
      if (!effective && this.allowDate()) return;

      const updatedFilters = this.updateFilters(filters);
      const title = reportRouteManager.navigationPath;
      const viewId = reportRouteManager.viewId;
      const report: IReport = this.selectedItem;
      this.loadReportOptionsModel();
      const options = this.reportOptionsModel?.options;
      const formats = this.readFormats();
      const secondaryFilters = reportRouteManager.reportSecondaryFilters;
      const groupBy = this.groupBy(report);
      const sortBy = this.sortBy(report);
      this.setReportExtension(formats);
      const reportName = this.buildReportName(report.name, this.reportExtension);
      this.renderedReportId = await DataQueries.runReport(
        { reportId: report.reportId, name: reportName, type: report.type, title },
        effective,
        updatedFilters,
        groupBy,
        sortBy,
        viewId,
        options,
        formats,
        secondaryFilters,
      );
    } finally {
      this.rendering = false;
    }
  }

  updateFilters(originalFilters: ScheduleFilterInput | undefined): ReportFilterInput | undefined {
    if (!originalFilters) return undefined;

    const filters: ScheduleFilterInput = Object.assign({}, originalFilters);
    if (originalFilters.timeOffStatus) {
      if (!filters.custom) filters.custom = [];
      filters.custom.push({
        field: 'timeoffStatus',
        eq: filters.timeOffStatus,
      });
      delete filters.timeOffStatus;
    }
    if (originalFilters.timeOffTypes) {
      if (!filters.custom) filters.custom = [];
      filters.custom.push({
        field: 'timeOffTypes',
        in: filters.timeOffTypes?.map((t: INameEntity) => t.id),
      });
      delete filters.timeOffTypes;
    }
    if (originalFilters.levels) {
      if (!filters.custom) filters.custom = [];
      filters.custom.push({
        field: 'levels',
        in: filters.levels?.map((t: MessageLevel) => t),
      });
      delete filters.levels;
    }
    if (originalFilters.availableBalance) {
      if (!filters.custom) filters.custom = [];
      filters.custom.push({
        field: 'availableBalance',
        eq: filters.availableBalance,
      });
      delete filters.availableBalance;
    }
    return filters;
  }

  readFormats(): IReportFormatInput | undefined {
    return {
      output: reportFormatsModel.selectedOutput,
      orientation: reportFormatsModel.selectedOrientation,
      paperSize: reportFormatsModel.selectedPaperSize,
    };
  }

  groupBy(report: IReport): string[] | undefined {
    return this.allowGrouping(report) ? RouteManagerBase.current.groups : undefined;
  }

  sortBy(report: IReport): ScheduleSort | undefined {
    return this.allowSorting(report) ? RouteManagerBase.current.sorts : undefined;
  }

  buildReportName(reportName: string, extension: ReportExtension): string {
    const newName = `${reportName.split('.')[0]}${extension}`;
    return newName;
  }

  setReportExtension(formats?: IReportFormatInput) {
    this.reportExtension = formats?.output === 'Excel' ? '.xlsx' : '.pdf';
  }

  isReportDownloadable(): boolean {
    return this.reportExtension !== '.pdf';
  }

  updateTitle() {} // do not remove!

  dateDurationDays(report?: IReport): number {
    const durationDays = report ? report.fields?.['duration:i'] : this.selectedItem?.fields?.['duration:i'];

    if (durationDays) return durationDays;
    if (this.allowQuarters(report)) return 92;
    if (this.allowMonth(report)) return 31;
    return 14;
  }

  allowDate(report?: IReport): boolean {
    const allow = report ? report.fields?.['allowDate:b'] : this.selectedItem ? this.selectedItem.fields?.['allowDate:b'] : true;
    return !!allow;
  }

  allowDuration(report?: IReport): boolean {
    const allow = report
      ? report.fields?.['allowDuration:b']
      : this.selectedItem
        ? this.selectedItem.fields?.['allowDuration:b']
        : true;
    return !!allow;
  }

  allowMonth(report?: IReport): boolean {
    const allow = report ? report.fields?.['allowMonth:b'] : this.selectedItem?.fields?.['allowMonth:b'];
    return !!allow;
  }

  allowQuarters(report?: IReport): boolean {
    const allow = report ? report.fields?.['allowQuarters:b'] : this.selectedItem?.fields?.['allowQuarters:b'];
    return !!allow;
  }

  allowFiltering(report?: IReport): boolean {
    return this.permittedFilters(report).length > 0;
  }

  allowGrouping(report?: IReport): boolean {
    return this.permittedGroups(report).length > 0;
  }

  allowSorting(report?: IReport): boolean {
    return this.permittedSorts(report).length > 0;
  }

  allowEmployeeUdfFilters(report?: IReport): boolean {
    const allow = report ? report.fields?.['allowEmployeeUdfFilters:b'] : this.selectedItem?.fields?.['allowEmployeeUdfFilters:b'];
    return !!allow;
  }

  allowEmployeeUdfGroups(report?: IReport): boolean {
    const allow = report ? report.fields?.['allowEmployeeUdfGroups:b'] : this.selectedItem?.fields?.['allowEmployeeUdfGroups:b'];
    return !!allow;
  }

  allowEmployeeUdfSorts(report?: IReport): boolean {
    const allow = report ? report.fields?.['allowEmployeeUdfSorts:b'] : this.selectedItem?.fields?.['allowEmployeeUdfSorts:b'];
    return !!allow;
  }

  permittedFilters(report?: IReport): string[] {
    const employeeUdfs = this.allowEmployeeUdfFilters(report)
      ? udfsSubscriptionService.subscriptionModel.getUdfFieldNames(['employees'])
      : [];

    const filters = (report ? report.fields?.['permittedFilters'] : this.selectedItem?.fields?.['permittedFilters']) ?? [];

    return [...filters, ...employeeUdfs];
  }

  permittedGroups(report?: IReport): string[] {
    const employeeUdfs = this.allowEmployeeUdfGroups(report)
      ? udfsSubscriptionService.subscriptionModel.getUdfFieldNames(['employees'])
      : [];

    const groups = (report ? report.fields?.['permittedGroups'] : this.selectedItem?.fields?.['permittedGroups']) ?? [];

    return [...groups, ...employeeUdfs];
  }

  permittedSorts(report?: IReport): string[] {
    const employeeUdfs = this.allowEmployeeUdfSorts()
      ? udfsSubscriptionService.subscriptionModel.getUdfFieldNames(['employees'])
      : [];

    const sorts = (report ? report.fields?.['permittedSorts'] : this.selectedItem?.fields?.['permittedSorts']) ?? [];

    return [...sorts, ...employeeUdfs];
  }

  permittedSecondaryFilters(report?: IReport): string[] {
    const secondaryFilters =
      (report ? report.fields?.['permittedSecondaryFilters'] : this.selectedItem?.fields?.['permittedSecondaryFilters']) ?? [];

    return secondaryFilters;
  }

  reasonFilterCategories(report?: IReport): ReasonCategory[] {
    const categories =
      (report ? report.fields?.['reasonFilterCategories'] : this.selectedItem?.fields?.['reasonFilterCategories']) ??
      Object.values(ReasonCategories);

    return [...categories];
  }

  loadReportOptionsModel() {
    const report: IReport = this.selectedItem;
    if (report) {
      this.reportOptionsModel =
        reportOptionsModelMap[report.name as keyof typeof reportOptionsModelMap] ?? reportOptionsModelMap['Default'];
      this.reportOptionsModel?.load();
    }
  }

  loadReportFormatsModel() {
    reportFormatsModel.load();
  }

  select = async (selectedItemId?: string | null): Promise<void> => {
    if (this.items.length === 0) {
      const reports = await DataQueries.getReports(schema);
      if (this.items.length === 0) this.items.push(...reports.data);
      this.activate();
    }
    if (!selectedItemId && !this.selectedItem) {
      this.loadReportOptionsModel();
      this.loadReportFormatsModel();
      return;
    }
    const selectedItem = this.state.items.find((f) => f.id === selectedItemId);
    await super.selectItem(selectedItem);
    this.loadReportOptionsModel();
    this.loadReportFormatsModel();
  };
}

const reportModel = new ReportModel();
export default reportModel;
